import React, { useState, useEffect, useRef } from "react";
import { db } from '../firebase';
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { uploadTrackImage } from "../lib/uploadImage";
import { parseTagsInput } from "../lib/tags";

const CreatePostModal = ({ isOpen, onClose, onPostCreated, track }) => {

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        post_url: '',
        image_url: '',
        platforms: [],
        tagsInput: ''
    });
    const [error, setError] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);

    const handleButtonClick = (platform) => {
        setFormData((prevState) => {
          const isSelected = prevState.platforms.includes(platform);
          const newPlatforms = isSelected
            ? prevState.platforms.filter((p) => p !== platform)
            : [...prevState.platforms, platform];

          return { ...prevState, platforms: newPlatforms };
        });
      };

    const handleChange = (e) => {
        const {name, value} = e.target;
        setFormData({...formData, [name]: value});
    };

    const handleFilePick = async (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = "";
        if (!file) return;
        if (!track) {
            setError("No track assigned. Cannot upload.");
            return;
        }
        setError(null);
        setUploading(true);
        try {
            const url = await uploadTrackImage({ file, track });
            setFormData((prev) => ({ ...prev, image_url: url }));
        } catch (err) {
            console.error("Upload failed", err);
            setError("Upload failed: " + err.message);
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        if (!track) {
            setError("No track assigned to this admin.");
            return;
        }
        try {
            const { tagsInput, ...rest } = formData;
            const tags = parseTagsInput(tagsInput);
            await addDoc(collection(db, "media"), {
                ...rest,
                tags,
                track,
                timestamp: serverTimestamp(),
                clicks: 0,
            });
            setFormData({ title: '', description: '', post_url: '', image_url: '', platforms: [], tagsInput: '' });
            onPostCreated();
        } catch(error) {
            setError("Error adding data. Please try again.");
            console.error("Error adding data: ", error);
        }
    };

    useEffect(() => {
        if(!isOpen) {
            setFormData({ title: '', description: '', post_url: '', image_url: '', platforms: [], tagsInput: '' });
            setError(null);
            setUploading(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    return (
        <div className="fixed z-50 top-0 left-0 w-full h-full bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-xl p-6 sm:w-3/4 md:w-2/3 lg:w-1/2 xl:w-1/2 max-h-full overflow-y-auto">
                <div className="flex justify-between items-center">
                    <button className="text-3xl" onClick={onClose}>&times;</button>
                    <div className="text-2xl"><strong>Add a new post</strong></div>
                    <div></div>
                </div>
                {track && (
                    <div className="mt-3 text-xs uppercase tracking-wider text-gray-500">
                        Posting to · <span className="text-gray-800 font-semibold">{track}</span>
                    </div>
                )}
                {error && <p className="mt-3 text-red-600 text-sm">{error}</p>}
                <form className="mt-6" onSubmit={handleSubmit}>
                    <div className="mb-6">
                        <input
                            type="text"
                            id="title"
                            name="title"
                            required
                            className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                            placeholder="Title"
                            value={formData.title}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-6">
                        <textarea
                            rows={5}
                            id="description"
                            name="description"
                            required
                            placeholder="Description"
                            className="bg-[#F1F3F4] w-full rounded-md py-3 px-4 resize-none"
                            value={formData.description}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-6">
                        <input
                            type="text"
                            id="post_url"
                            name="post_url"
                            required
                            placeholder="Post URL"
                            className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                            value={formData.post_url}
                            onChange={handleChange}
                        />
                    </div>
                    <div className="mb-6">
                        <div className="flex gap-2">
                            <input
                                type="text"
                                id="image_url"
                                name="image_url"
                                required
                                placeholder="Image URL (paste or upload)"
                                className="bg-[#F1F3F4] flex-1 rounded-md py-3 px-4"
                                value={formData.image_url}
                                onChange={handleChange}
                            />
                            <button
                                type="button"
                                disabled={uploading}
                                onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium px-4 rounded-md disabled:opacity-50"
                            >
                                {uploading ? "Uploading..." : "Upload"}
                            </button>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleFilePick}
                            />
                        </div>
                        {formData.image_url && (
                            <img
                                src={formData.image_url}
                                alt=""
                                className="mt-3 max-h-40 rounded-md object-contain bg-gray-50 border border-gray-100"
                            />
                        )}
                    </div>
                    <div className="mb-6">
                        <input
                            type="text"
                            id="tagsInput"
                            name="tagsInput"
                            className="bg-[#F1F3F4] w-full rounded-md py-3 px-4"
                            placeholder="Tags (comma-separated, optional)"
                            value={formData.tagsInput}
                            onChange={handleChange}
                        />
                        <p className="mt-1 text-xs text-gray-500 text-left">
                            Lowercase, hyphens ok. Used by /share?tag=&lt;tag&gt; to bundle posts.
                        </p>
                    </div>
                    <div className="mb-6">
                        <span className="block text-left font-bold text-xl mb-2">Share To</span>
                        <div className="flex flex-wrap gap-2">
                            {['Facebook', 'X', 'Instagram', 'Threads', 'TikTok', 'WhatsApp', 'LinkedIn'].map((platform) => (
                            <button
                                key={platform}
                                type="button"
                                className={`px-4 py-2 rounded-full ${
                                formData.platforms.includes(platform.toLowerCase()) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                }`}
                                onClick={() => handleButtonClick(platform.toLowerCase())}
                            >
                                {platform}
                            </button>
                            ))}
                        </div>
                    </div>
                    <button type="submit" disabled={uploading} className="w-full bg-sky-500 text-white py-3 rounded-lg hover:bg-sky-600 disabled:opacity-50" >Create</button>
                </form>
            </div>
        </div>
    );
};
export default CreatePostModal;
